/**
 * URL allowlist for authored content blocks.
 *
 * Round-2 review finding 3: a stored XSS path exists when an authored CTA /
 * hero link is `javascript:alert(...)` (or `data:`, `vbscript:`) and the
 * renderer hands it to `<Link to={...}>`: React Router renders an anchor
 * with that href, and clicking it executes the script in the visitor's
 * browser. The threat model is a compromised tenant admin attacking their
 * own tenant's visitors; blast radius is the visitors of that single page.
 *
 * Defense is to allowlist the URL schemes we render. The allowlist is the
 * same on the server (in `validateBlocks`, so bad URLs never persist) and
 * on the client (in `RichTextEditor.setLink`, `CtaButtonBlock`, and
 * `HeroBlock`, so even URLs that somehow got into the DB before this fix
 * landed are defanged on render). Both layers must agree, so the rule
 * lives here as one shared function the client imports and the server
 * mirrors via `server/src/utils/url-validator.js`.
 *
 * Allowed:
 *   - http: and https: absolute URLs
 *   - mailto: and tel: links (handed off to the OS, never SPA-routed)
 *   - relative paths beginning with `/` or `./` (these resolve against the
 *     site, never escape to a foreign origin under <Link>)
 *   - fragment-only links beginning with `#`
 *
 * Rejected:
 *   - javascript: / data: / vbscript: / file: / chrome: / about:, any
 *     scheme that can execute code or escape the page sandbox
 *   - schemeless absolute URLs (`//attacker.example/foo`): these are
 *     "protocol-relative" and Link treats them as same-origin paths but
 *     the browser will load them as foreign-origin URLs
 *   - any value containing a backslash (`/\attacker.example`): browsers
 *     normalize `\` to `/`, so it resolves to the protocol-relative form
 *   - non-strings, empty strings, whitespace-only strings
 */

import he from 'he';

const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/**
 * v1.0.1 ticket #44: HTML-entity decode pass to defang values like
 * `&#x6a;avascript:alert(1)` that resolve to a banned scheme only AFTER
 * the browser's HTML parser entity-decodes them.
 *
 * Why this is needed: when a malicious href slips into authored HTML
 * (e.g. via a paste in the paragraph block), the value reaches the
 * validators as the raw entity-encoded form. `new URL('&#x6a;avascript:..',
 * base)` parses that as a relative path (no recognizable scheme prefix)
 * and the origin-match branch waves it through. By the time it reaches
 * the browser, the entity is decoded and the href is `javascript:...`.
 *
 * Decoding before validation closes the gap. We decode once and, if
 * decoding changed the value, recurse: `he.decode` is one-pass, so a
 * value double-encoded as `&amp;#x6a;avascript:...` needs the second
 * pass to surface as the live banned form. A small bound on the
 * recursion depth keeps us safe against pathological inputs.
 *
 * @param {string} value
 * @returns {string}
 */
function decodeHtmlEntitiesIfPresent(value) {
    let current = value;
    for (let i = 0; i < 5; i += 1) {
        const next = he.decode(current);
        if (next === current) return current;
        current = next;
    }
    return current;
}

/**
 * Return true if `value` is a string the renderer is allowed to use as a
 * link href. See module header for the precise allowlist.
 *
 * @param {*} value - Candidate URL.
 * @returns {boolean}
 */
export function isAllowedLinkUrl(value) {
    if (typeof value !== 'string') {
        return false;
    }
    const decoded = decodeHtmlEntitiesIfPresent(value);
    const trimmed = decoded.trim();
    if (!trimmed) {
        return false;
    }

    // Protocol-relative URLs (`//host/path`) are foreign-origin loads that
    // bypass `<Link>`'s same-site assumption; reject explicitly.
    if (trimmed.startsWith('//')) {
        return false;
    }
    // Backslashes never appear in a legitimate href. Browsers normalize `\`
    // to `/`, so `/\evil.com` resolves to the protocol-relative `//evil.com`
    // and slips past the `//` check above; reject any backslash outright.
    if (trimmed.includes('\\')) {
        return false;
    }

    // Relative paths and fragments are always safe: they cannot specify a
    // different origin or a non-HTTP scheme.
    if (
        trimmed.startsWith('/') ||
        trimmed.startsWith('./') ||
        trimmed.startsWith('#')
    ) {
        return true;
    }

    // Absolute URL: the scheme must be in the allowlist. Use URL parsing
    // rather than a regex so we correctly handle uppercase schemes
    // (`JAVASCRIPT:alert(1)`), embedded whitespace, and exotic encodings:
    // the parser normalizes them all before the `.protocol` check.
    try {
        // A base is required so the URL parser accepts relative inputs as
        // valid; using `https://example.invalid` keeps the parse honest
        // for absolute URLs (which ignore the base) while letting bare
        // schemeless inputs fall through to the relative-path branches
        // above (which we've already handled, anything reaching here is
        // either an absolute URL with a scheme or a bareword like "foo"
        // that the parser would resolve as relative).
        const parsed = new URL(trimmed, 'https://example.invalid');

        // A bareword like "foo" parses as `https://example.invalid/foo`:
        // the resulting origin is the base, which means it's effectively
        // a relative path. Treat that as allowed (Link will render it
        // as a relative path).
        if (parsed.origin === 'https://example.invalid') {
            return true;
        }

        return ALLOWED_SCHEMES.includes(parsed.protocol);
    } catch {
        return false;
    }
}

/**
 * Return true if `value` is a string the renderer is allowed to use as an
 * image URL (`<img src>` or CSS `url("...")`). Stricter than the link
 * allowlist: only http:/https: absolute URLs and same-site relative paths
 * are accepted. `mailto:` is not a valid image source, and `data:` URIs
 * are rejected because they would let an admin embed arbitrary
 * (potentially huge) payloads in DOM and CSS, blowing past the per-block
 * size cap.
 *
 * @param {*} value - Candidate image URL.
 * @returns {boolean}
 */
export function isAllowedImageUrl(value) {
    if (typeof value !== 'string') {
        return false;
    }
    const decoded = decodeHtmlEntitiesIfPresent(value);
    const trimmed = decoded.trim();
    if (!trimmed) {
        return false;
    }
    if (trimmed.startsWith('//')) {
        return false;
    }
    // Backslashes never appear in a legitimate image URL. Browsers
    // normalize `\` to `/`, so `/\evil.com` resolves to the
    // protocol-relative `//evil.com`; reject any backslash outright.
    if (trimmed.includes('\\')) {
        return false;
    }
    // Fragment-only inputs (`#x`) are not valid image sources. Handle
    // before the URL parse, otherwise `new URL('#x', base)` resolves
    // with origin === base and would incorrectly accept it.
    if (trimmed.startsWith('#')) {
        return false;
    }
    if (trimmed.startsWith('/') || trimmed.startsWith('./')) {
        return true;
    }
    try {
        const parsed = new URL(trimmed, 'https://example.invalid');
        if (parsed.origin === 'https://example.invalid') {
            return true;
        }
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * CSS-escape a string for safe interpolation into a CSS `url("...")` token.
 * Used by `HeroBlock` when pushing an author-supplied background image URL
 * onto a CSS custom property (round-2 review finding 4: CSS injection).
 *
 * Prefers the native `CSS.escape` (available in every modern browser and in
 * jsdom 22+) and falls back to a manual escape for tests / SSR contexts
 * that lack a `CSS` global.
 *
 * @param {string} value - Raw string.
 * @returns {string} A CSS-safe escaped form.
 */
export function cssEscape(value) {
    if (typeof value !== 'string') {
        return '';
    }
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }
    // Minimal manual fallback: only chars meaningful inside a CSS `url("...")`
    // token need escaping for the injection vector this guard exists for.
    return value.replace(/[\\"]/g, (m) => `\\${m}`);
}

/**
 * Return true if the link should be rendered as a plain `<a href>` rather
 * than a React Router `<Link to>`. Anchors include:
 *
 *   - absolute `http(s)://` URLs: external sites; should open in a new tab.
 *   - `mailto:` and `tel:` URLs: non-navigational schemes that React Router
 *     would otherwise treat as a relative SPA path and route to a SPA 404
 *     (round-4 review finding 8: a CTA pointing at `mailto:foo@bar.com`
 *     was rendering an in-app navigation to `/<basename>/mailto:foo@bar.com`
 *     instead of opening the user's mail client).
 *
 * Any other URL (relative path, hash fragment) belongs in <Link>. Callers
 * should still validate the URL via `isAllowedLinkUrl` before rendering.
 *
 * @param {string} value - URL string previously validated by isAllowedLinkUrl.
 * @returns {boolean}
 */
export function isRawAnchorUrl(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim().toLowerCase();
    return (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('mailto:') ||
        trimmed.startsWith('tel:')
    );
}

/**
 * Return true if the link should open in a new tab. Only http(s) external
 * URLs get `target="_blank"`: `mailto:` / `tel:` are handled by the OS and
 * shouldn't carry a tab target.
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isExternalHttpUrl(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim().toLowerCase();
    return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

export default isAllowedLinkUrl;
